// ===================================================================
// NewAppointmentModal.tsx - Modal para crear turnos (Asistentes)
// CON Calendario Personalizado + Horarios del Profesional
// ===================================================================

import React, { useState, useEffect, useRef } from 'react';
import { CajaMotivo, motivoDelDia, pedirDiasDelServicio, MotivosDias } from './MotivoDelDia';
import {
  X,
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
  FileText,
  Loader,
  Check,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  MapPin
} from 'lucide-react';

// --- Interfaces ---
interface Professional {
  professional_id: number;
  nombre: string;
  apellido: string;
  especialidad: string;
}

interface Service {
  id: number;
  name: string;
  duration: number;
  price: number;
}

interface ScheduleDay {
  day: string;
  isWorking: boolean;
  startTime?: string;
  endTime?: string;
}

interface LocationOption {
  id: number;
  name: string;
  address?: string;
  city?: string;
}

interface NewAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  professionals: Professional[];
  preselectedProfessional?: number;
  preselectedDate?: string;
}

// ===================================================================
// COMPONENTE: Calendario Personalizado (mismo estilo que la landing)
// ===================================================================
const CustomCalendar: React.FC<{
  selectedDate: string;
  onDateSelect: (date: string) => void;
  professionalSchedule: ScheduleDay[];
  blockedDates?: string[];
  specialDayDates?: string[];
  onlySpecialDays?: boolean;
  maxDate?: string;
  /** Días habilitados para el servicio elegido (contempla su horario propio,
   *  que puede abrir días fuera del horario general). null = usar el general. */
  serviceAvailableDates?: string[] | null;
  /** Por qué no hay turnos cada día (lo calcula el backend para el panel). */
  motivos?: MotivosDias;
}> = ({ selectedDate, onDateSelect, professionalSchedule, blockedDates = [], specialDayDates = [], onlySpecialDays = false, maxDate, serviceAvailableDates = null, motivos = {} }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  // El día gris que tocaron, para mostrar por qué no tiene turnos
  const [motivoVisible, setMotivoVisible] = useState<{ fecha: string; texto: string } | null>(null);

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const isDateAvailable = (date: Date): boolean => {
    const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    // Verificar si está bloqueada
    if (blockedDates.includes(dateStr)) return false;
    // Día especial siempre disponible
    if (specialDayDates.includes(dateStr)) return true;
    // Si solo días especiales, el resto no está disponible
    if (onlySpecialDays) return false;

    // Con servicio elegido mandan los días que calculó el backend (ya resuelve
    // el horario propio del servicio, que puede habilitar días no laborales).
    if (serviceAvailableDates !== null) {
      return serviceAvailableDates.includes(dateStr);
    }

    return esDiaLaboral(date);
  };

  // ¿Ese día de la semana se atiende según el horario general?
  const esDiaLaboral = (date: Date): boolean => {
    if (!professionalSchedule || professionalSchedule.length === 0) {
      const day = date.getDay();
      return day >= 1 && day <= 6;
    }
    const dayOfWeek = date.getDay();
    const daySchedule = professionalSchedule.find(d => {
      const dayMapping: Record<string, number> = {
        'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
        'thursday': 4, 'friday': 5, 'saturday': 6
      };
      return dayMapping[d.day] === dayOfWeek;
    });
    return daySchedule ? daySchedule.isWorking : false;
  };

  const isDateBlocked = (date: Date): boolean => {
    const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    return blockedDates.includes(dateStr);
  };

  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isBeyondMax = (date: Date): boolean => {
    if (!maxDate) return false;
    const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    return dateStr > maxDate;
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    const days = [];
    const currentDate = new Date(startDate);
    for (let i = 0; i < 42; i++) {
      const date = new Date(currentDate);
      const isCurrentMonth = date.getMonth() === month;
      const isAvailable = isCurrentMonth && isDateAvailable(date) && !isPastDate(date) && !isBeyondMax(date);
      const isBlocked = isCurrentMonth && !isPastDate(date) && isDateBlocked(date);
      const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
      const isSpecialDay = specialDayDates.includes(dateStr);
      const motivo = isCurrentMonth && !isAvailable && !isPastDate(date)
        ? motivoDelDia({ fecha: dateStr, diaSemana: date.getDay(), motivos, maxDate, bloqueada: blockedDates.includes(dateStr), soloEspeciales: onlySpecialDays, diaLaboral: esDiaLaboral(date) })
        : null;
      days.push({
        date, dateStr, day: date.getDate(), isCurrentMonth, isAvailable, isBlocked, isSpecialDay, motivo,
        isPast: isPastDate(date), isSelected: dateStr === selectedDate,
        isToday: date.toDateString() === new Date().toDateString()
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return days;
  };

  const days = generateCalendarDays();

  const handleDayClick = (dayInfo: any) => {
    if (dayInfo.isAvailable) {
      setMotivoVisible(null);
      const d = dayInfo.date;
      onDateSelect(`${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`);
    } else if (dayInfo.motivo) {
      setMotivoVisible({ fecha: dayInfo.dateStr, texto: dayInfo.motivo });
    }
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} className="p-1 hover:bg-gray-200 rounded">
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
        <h3 className="text-sm font-semibold text-gray-900">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button type="button" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} className="p-1 hover:bg-gray-200 rounded">
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {dayNames.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">{day}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((dayInfo, index) => {
          let cls = "w-8 h-8 flex items-center justify-center text-xs rounded-md cursor-pointer transition-all mx-auto ";
          if (!dayInfo.isCurrentMonth) cls += "text-gray-300 cursor-not-allowed ";
          else if (dayInfo.isPast) cls += "text-gray-400 cursor-not-allowed bg-gray-100 ";
          else if (dayInfo.isBlocked) cls += "text-red-500 bg-red-100 cursor-help line-through font-medium ";
          else if (dayInfo.isAvailable && dayInfo.isSpecialDay) {
            cls += dayInfo.isSelected ? "bg-blue-600 text-white font-semibold shadow-md " : "bg-emerald-200 text-emerald-900 hover:bg-emerald-300 font-bold ring-1 ring-emerald-400 ";
          } else if (dayInfo.isAvailable) {
            cls += dayInfo.isSelected ? "bg-blue-600 text-white font-semibold shadow-md " : "bg-green-100 text-green-800 hover:bg-green-200 font-medium ";
          } else cls += "text-gray-400 bg-red-50 cursor-help hover:bg-red-100 ";
          if (motivoVisible && motivoVisible.fecha === dayInfo.dateStr) cls += "ring-2 ring-amber-400 ";
          else if (dayInfo.isToday && !dayInfo.isSelected) cls += "ring-2 ring-blue-300 ";
          return (
            <button
              key={index}
              type="button"
              onClick={() => handleDayClick(dayInfo)}
              disabled={!dayInfo.isCurrentMonth || (!dayInfo.isAvailable && !dayInfo.motivo)}
              aria-disabled={!dayInfo.isAvailable || undefined}
              title={dayInfo.motivo || undefined}
              className={cls}
            >
              {dayInfo.day}
            </button>
          );
        })}
      </div>
      {motivoVisible && (
        <CajaMotivo fecha={motivoVisible.fecha} texto={motivoVisible.texto} onCerrar={() => setMotivoVisible(null)} />
      )}
      <div className="mt-2 flex items-center justify-center space-x-3 text-xs flex-wrap gap-y-1">
        <div className="flex items-center"><div className="w-2.5 h-2.5 bg-green-100 rounded mr-1"></div><span className="text-gray-500">Disponible</span></div>
        <div className="flex items-center"><div className="w-2.5 h-2.5 bg-red-50 rounded mr-1 border border-red-100"></div><span className="text-gray-500">No disponible</span></div>
        {blockedDates.length > 0 && (
          <div className="flex items-center"><div className="w-2.5 h-2.5 bg-red-100 border border-red-300 rounded mr-1"></div><span className="text-gray-500">Bloqueado</span></div>
        )}
        {specialDayDates.length > 0 && (
          <div className="flex items-center"><div className="w-2.5 h-2.5 bg-emerald-200 border border-emerald-400 rounded mr-1"></div><span className="text-gray-500">Día especial</span></div>
        )}
        <div className="flex items-center"><div className="w-2.5 h-2.5 bg-blue-600 rounded mr-1"></div><span className="text-gray-500">Seleccionado</span></div>
      </div>
      {!motivoVisible && (
        <p className="mt-1.5 text-center text-[11px] text-gray-400">Tocá un día no disponible para ver por qué.</p>
      )}
    </div>
  );
};

// ===================================================================
// COMPONENTE PRINCIPAL
// ===================================================================
const NewAppointmentModal: React.FC<NewAppointmentModalProps> = ({
  isOpen, onClose, onSuccess, professionals, preselectedProfessional, preselectedDate
}) => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [schedule, setSchedule] = useState<ScheduleDay[]>([]);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [specialDayDates, setSpecialDayDates] = useState<string[]>([]);
  const [onlySpecialDays, setOnlySpecialDays] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [whatsappSent, setWhatsappSent] = useState(false);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [paymentConfig, setPaymentConfig] = useState<{
    tipo_cobro?: string;
    porcentaje_sena?: number;
    acepta_transferencia?: boolean;
    acepta_mercadopago?: boolean;
    acepta_efectivo?: boolean;
  } | null>(null);

  // Campos dinámicos del booking
  const [bookingExtraFields, setBookingExtraFields] = useState<any>(null);
  const [patientExtraData, setPatientExtraData] = useState<Record<string, string>>({});

  // Sobreturnos
  const [maxOverridesPerSlot, setMaxOverridesPerSlot] = useState(0);
  const [maxAdvanceBooking, setMaxAdvanceBooking] = useState(30);
  const [bookedSlots, setBookedSlots] = useState<Record<string, number>>({}); // { "09:00": 1, "10:00": 2 }
  const [isOverride, setIsOverride] = useState(false);

  const getTodayStr = () => {
    const t = new Date();
    return `${t.getFullYear()}-${(t.getMonth() + 1).toString().padStart(2, '0')}-${t.getDate().toString().padStart(2, '0')}`;
  };

  // Días habilitados para el servicio elegido (respeta su horario propio)
  const [serviceAvailableDates, setServiceAvailableDates] = useState<string[] | null>(null);
  const [motivosDias, setMotivosDias] = useState<MotivosDias>({});
  const [formData, setFormData] = useState({
    professionalId: preselectedProfessional || '' as number | '',
    locationId: '' as number | '',
    serviceId: '' as number | '',
    serviceIds: [] as number[],
    appointmentDate: preselectedDate || getTodayStr(),
    appointmentTime: '',
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    notes: ''
  });

  // 🆕 Comprobante de pago (se sube al crear; queda auto-verificado)
  const [comprobanteFile, setComprobanteFile] = useState<File | null>(null);
  const [uploadingComprobante, setUploadingComprobante] = useState(false);

  // 🆕 Autocompletado de pacientes anteriores (del profesional seleccionado)
  // Se cargan TODOS una vez al elegir profesional y se filtran en el navegador
  // (instantáneo, sin red por cada tecla).
  const [allPatients, setAllPatients] = useState<any[]>([]);
  const [patientSuggestions, setPatientSuggestions] = useState<any[]>([]);
  const [showPatientSuggestions, setShowPatientSuggestions] = useState(false);
  const skipPatientSearchRef = useRef(false); // evita re-buscar tras seleccionar

  // Carga (una vez) todos los pacientes anteriores del profesional para filtrar local
  const loadAllPatients = async (profId: number) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/assistants/professional/${profId}/patients`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success && Array.isArray(data.patients)) {
        setAllPatients(data.patients);
      }
    } catch { /* silencioso: si falla, el autocompletado simplemente no sugiere */ }
  };

  useEffect(() => {
    if (preselectedProfessional) {
      setFormData(prev => ({ ...prev, professionalId: preselectedProfessional }));
      loadLocations(preselectedProfessional);
      loadServices(preselectedProfessional);
      loadPaymentConfig(preselectedProfessional);
      loadBookingExtraFields(preselectedProfessional);
      loadAllPatients(preselectedProfessional);
    }
  }, [preselectedProfessional]);

  useEffect(() => {
    if (preselectedDate) setFormData(prev => ({ ...prev, appointmentDate: preselectedDate }));
  }, [preselectedDate]);

  useEffect(() => {
    if (formData.professionalId && formData.appointmentDate) loadAvailableSlots();
  }, [formData.appointmentDate, formData.professionalId, formData.serviceId, formData.serviceIds, formData.locationId]);

  // Días habilitados para el servicio elegido: el backend ya resuelve si el
  // servicio tiene horario propio (que puede abrir días fuera del horario
  // general del profesional) o si sigue el horario normal.
  useEffect(() => {
    const svcId = formData.serviceIds.length > 0 ? formData.serviceIds[0] : formData.serviceId;
    if (!formData.professionalId || !svcId) {
      setServiceAvailableDates(null);
      setMotivosDias({});
      return;
    }
    let cancelled = false;
    setServiceAvailableDates(null);
    setMotivosDias({});
    const today = new Date();
    const endDate = new Date();
    // Hasta donde se puede reservar. Eran 60 días fijos: con 90 o 180, los
    // días que pasaban de 60 salían "No disponible" (Vero, oct-2026).
    endDate.setDate(today.getDate() + maxAdvanceBooking);
    const toStr = (d: Date) => `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
    const locParam = formData.locationId ? `&locationId=${formData.locationId}` : '';
    // Si falla, queda null y usa el horario general
    pedirDiasDelServicio(API_URL, formData.professionalId, `?start=${toStr(today)}&end=${toStr(endDate)}&serviceId=${svcId}${locParam}`)
      .then((r) => {
        if (cancelled || !r) return;
        setServiceAvailableDates(r.fechas);
        setMotivosDias(r.motivos);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.professionalId, formData.serviceId, formData.serviceIds, formData.locationId, maxAdvanceBooking]);

  // 🆕 Filtrar pacientes localmente mientras se escribe el nombre (instantáneo)
  useEffect(() => {
    // Si acabamos de seleccionar un paciente, no re-abrir el desplegable
    if (skipPatientSearchRef.current) { skipPatientSearchRef.current = false; return; }
    const name = formData.clientName.trim().toLowerCase();
    if (name.length < 2 || allPatients.length === 0) {
      setPatientSuggestions([]); setShowPatientSuggestions(false);
      return;
    }
    const matches = allPatients.filter(p =>
      (p.clientName || '').toLowerCase().includes(name) ||
      (p.clientPhone || '').toLowerCase().includes(name)
    ).slice(0, 8);
    setPatientSuggestions(matches);
    setShowPatientSuggestions(matches.length > 0);
  }, [formData.clientName, allPatients]);

  // 🆕 Al elegir un paciente anterior, precargar sus datos
  const selectPatient = (p: any) => {
    skipPatientSearchRef.current = true;
    setFormData(prev => ({
      ...prev,
      clientName: p.clientName || prev.clientName,
      clientPhone: p.clientPhone || prev.clientPhone,
      clientEmail: p.clientEmail || prev.clientEmail
    }));
    // Precargar solo los campos extra que el profesional tiene configurados
    if (p.patientExtraData && bookingExtraFields?.fields) {
      const allowed: Record<string, any> = {};
      bookingExtraFields.fields.forEach((f: any) => {
        if (p.patientExtraData[f.key] != null && p.patientExtraData[f.key] !== '') {
          allowed[f.key] = p.patientExtraData[f.key];
        }
      });
      if (Object.keys(allowed).length > 0) setPatientExtraData(prev => ({ ...prev, ...allowed }));
    }
    setShowPatientSuggestions(false);
    setPatientSuggestions([]);
  };

  const loadLocations = async (profId: number) => {
    try {
      setLoadingLocations(true);
      // Usar endpoint público que devuelve sedes activas
      const res = await fetch(`${API_URL}/api/locations/professional/${profId}/public`);
      const data = await res.json();
      if (data.success && data.locations) {
        setLocations(data.locations);
        // Si hay una sola sede, seleccionarla automáticamente
        if (data.locations.length === 1) {
          const loc = data.locations[0];
          setFormData(prev => ({ ...prev, locationId: loc.id }));
          loadSchedule(profId, loc.id);
        } else if (data.locations.length > 1) {
          // Múltiples sedes: resetear selección para que elija
          setFormData(prev => ({ ...prev, locationId: '' }));
          setSchedule([]);
          setBlockedDates([]);
        } else {
          // Sin sedes: cargar schedule sin locationId
          loadSchedule(profId);
        }
      } else {
        setLocations([]);
        loadSchedule(profId);
      }
    } catch (err) {
      console.error('Error cargando sedes:', err);
      setLocations([]);
      loadSchedule(profId);
    } finally {
      setLoadingLocations(false);
    }
  };

  const loadPaymentConfig = async (profId: number) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/config/${profId}`);
      if (response.ok) {
        const data = await response.json();
        setPaymentConfig(data.data || null);
      } else {
        setPaymentConfig(null);
      }
    } catch (error) {
      console.error('Error cargando config de pagos:', error);
      setPaymentConfig(null);
    }
  };

  const loadBookingExtraFields = async (profId: number) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/professionals/${profId}/booking-extra-fields`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.booking_extra_fields?.enabled && data.booking_extra_fields.fields?.length > 0) {
        setBookingExtraFields(data.booking_extra_fields);
      } else {
        setBookingExtraFields(null);
      }
    } catch (err) {
      console.error('Error cargando campos extra:', err);
    }
  };

  const loadServices = async (profId: number) => {
    try {
      setLoadingServices(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/assistants/professional/${profId}/services`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        setServices(data.services);
        // No auto-seleccionar - dejar que el usuario elija los servicios
      }
    } catch (err) { console.error('Error cargando servicios:', err); }
    finally { setLoadingServices(false); }
  };

  const loadSchedule = async (profId: number, locId?: number) => {
    try {
      setLoadingSchedule(true);
      const token = localStorage.getItem('token');
      const locationParam = locId ? `?locationId=${locId}` : '';
      const res = await fetch(`${API_URL}/api/assistants/professional/${profId}/schedule${locationParam}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success && data.schedule) setSchedule(data.schedule);

      // Cargar config de sobreturnos + onlySpecialDays
      try {
        const locationConfigParam = locId ? `?locationId=${locId}` : '';
        const configRes = await fetch(`${API_URL}/api/schedule/professional/${profId}/config/public${locationConfigParam}`);
        if (configRes.ok) {
          const configData = await configRes.json();
          if (configData.success && configData.scheduleConfig) {
            setMaxOverridesPerSlot(configData.scheduleConfig.maxOverridesPerSlot || 0);
            if (configData.scheduleConfig.maxAdvanceBooking) {
              setMaxAdvanceBooking(configData.scheduleConfig.maxAdvanceBooking);
            }
            setOnlySpecialDays(configData.scheduleConfig.onlySpecialDays || false);
          }
        }
      } catch (cfgErr) { console.error('Error cargando config sobreturnos:', cfgErr); }

      // Cargar días bloqueados (con filtro de sede)
      try {
        const excLocationParam = locId ? `?locationId=${locId}` : '';
        const excRes = await fetch(`${API_URL}/api/schedule/professional/${profId}/exceptions/public${excLocationParam}`);
        if (excRes.ok) {
          const excData = await excRes.json();
          if (excData.success && excData.exceptions) {
            const blocked = excData.exceptions
              .filter((e: any) => !e.is_working)
              .map((e: any) => typeof e.exception_date === 'string' ? e.exception_date.split('T')[0] : new Date(e.exception_date).toISOString().split('T')[0]);
            setBlockedDates(blocked);
            const special = excData.exceptions
              .filter((e: any) => e.is_working && e.exception_type === 'custom_hours')
              .map((e: any) => typeof e.exception_date === 'string' ? e.exception_date.split('T')[0] : new Date(e.exception_date).toISOString().split('T')[0]);
            setSpecialDayDates(special);
          }
        }
      } catch (excErr) { console.error('Error cargando excepciones:', excErr); }
    } catch (err) { console.error('Error cargando horarios:', err); setSchedule([]); }
    finally { setLoadingSchedule(false); }
  };

  // --- Cargar horarios REALES disponibles (misma lógica que la landing pública) ---
  const [loadingSlots, setLoadingSlots] = useState(false);

  const loadAvailableSlots = async () => {
    if (!formData.professionalId || !formData.appointmentDate) return;

    try {
      setLoadingSlots(true);
      setIsOverride(false);
      const dateStr = formData.appointmentDate;
      const activeServiceId = formData.serviceIds.length > 0 ? formData.serviceIds[0] : formData.serviceId;
      const serviceParam = activeServiceId ? `&serviceId=${activeServiceId}` : '';
      const locationParam = formData.locationId ? `&locationId=${formData.locationId}` : '';

      // 1. Cargar slots disponibles (mismo endpoint que la landing pública)
      const res = await fetch(
        `${API_URL}/api/appointments/professional/${formData.professionalId}/available-slots?date=${dateStr}${serviceParam}${locationParam}`
      );
      const data = await res.json();

      let freeSlots: string[] = [];
      const occupiedMap: Record<string, number> = {};

      if (data.success && data.availableSlots) {
        // Separar slots libres y ocupados
        for (const slot of data.availableSlots) {
          if (slot.available) {
            freeSlots.push(slot.time);
          } else {
            // Slot ocupado — potencial sobreturno
            occupiedMap[slot.time] = (occupiedMap[slot.time] || 0) + 1;
          }
        }
      }

      // 2. Si hay sobreturnos habilitados, cargar también la config y los turnos existentes
      if (maxOverridesPerSlot > 0) {
        try {
          const token = localStorage.getItem('token');
          const apptsRes = await fetch(
            `${API_URL}/api/appointments/professional/${formData.professionalId}?date=${dateStr}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          const apptsData = await apptsRes.json();
          if (apptsData.success && apptsData.appointments) {
            // Contar turnos activos por horario
            const countMap: Record<string, number> = {};
            for (const appt of apptsData.appointments) {
              if (appt.status !== 'cancelled' && appt.status !== 'completed') {
                const time = appt.appointment_time?.substring(0, 5);
                if (time) countMap[time] = (countMap[time] || 0) + 1;
              }
            }
            setBookedSlots(countMap);
          }
        } catch (err) {
          console.error('Error cargando turnos para sobreturnos:', err);
        }
      } else {
        setBookedSlots({});
      }

      setAvailableTimeSlots(freeSlots);

      // Seleccionar primer slot libre si no hay selección válida
      if (freeSlots.length > 0 && !freeSlots.includes(formData.appointmentTime)) {
        setFormData(prev => ({ ...prev, appointmentTime: freeSlots[0] }));
      }
      if (freeSlots.length === 0 && !isOverride) {
        setFormData(prev => ({ ...prev, appointmentTime: '' }));
      }
    } catch (err) {
      console.error('Error cargando slots disponibles:', err);
      setAvailableTimeSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleProfessionalChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const profId = parseInt(e.target.value);
    setFormData(prev => ({ ...prev, professionalId: profId, locationId: '', serviceId: '', serviceIds: [], appointmentTime: '' }));
    setServices([]); setSchedule([]); setLocations([]); setBlockedDates([]); setPaymentConfig(null);
    setBookingExtraFields(null); setPatientExtraData({});
    setAllPatients([]); setPatientSuggestions([]); setShowPatientSuggestions(false);
    if (profId) { loadLocations(profId); loadServices(profId); loadPaymentConfig(profId); loadBookingExtraFields(profId); loadAllPatients(profId); }
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const locId = parseInt(e.target.value);
    setFormData(prev => ({ ...prev, locationId: locId, appointmentDate: getTodayStr(), appointmentTime: '' }));
    setSchedule([]); setBlockedDates([]); setAvailableTimeSlots([]);
    if (locId && formData.professionalId) {
      loadSchedule(formData.professionalId as number, locId);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleDateSelect = (dateStr: string) => {
    setFormData(prev => ({ ...prev, appointmentDate: dateStr, appointmentTime: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.professionalId || (formData.serviceIds.length === 0 && !formData.serviceId) || !formData.clientName) { setError('Profesional, servicio y nombre son requeridos'); return; }
    if (!formData.appointmentDate) { setError('Seleccioná una fecha en el calendario'); return; }
    if (!formData.appointmentTime) { setError('Seleccioná un horario'); return; }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // 🆕 Si la secre adjuntó comprobante, lo subimos primero y mandamos la URL.
      // El backend lo deja auto-verificado (pago_verificado=true).
      let comprobantePagoUrl: string | null = null;
      if (comprobanteFile) {
        try {
          setUploadingComprobante(true);
          const fd = new FormData();
          fd.append('proof', comprobanteFile);
          const upRes = await fetch(`${API_URL}/api/payments/verification/upload-proof`, {
            method: 'POST',
            body: fd
          });
          const upData = await upRes.json();
          if (upData.success && upData.proofUrl) {
            comprobantePagoUrl = upData.proofUrl;
          } else {
            setError('No se pudo subir el comprobante. Probá de nuevo o creá el turno sin él.');
            setLoading(false);
            setUploadingComprobante(false);
            return;
          }
        } finally {
          setUploadingComprobante(false);
        }
      }

      const res = await fetch(`${API_URL}/api/assistants/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          professionalId: formData.professionalId,
          serviceId: formData.serviceIds[0] || formData.serviceId,
          serviceIds: formData.serviceIds.length > 0 ? formData.serviceIds : undefined,
          appointmentDate: formData.appointmentDate,
          appointmentTime: formData.appointmentTime,
          clientName: formData.clientName,
          clientPhone: formData.clientPhone || null,
          clientEmail: formData.clientEmail || null,
          notes: formData.notes || null,
          locationId: formData.locationId || null,
          isOverride: isOverride,
          comprobantePagoUrl,
          ...(bookingExtraFields && Object.keys(patientExtraData).some(k => patientExtraData[k]) && {
            patient_extra_data: Object.fromEntries(Object.entries(patientExtraData).filter(([_, v]) => v))
          })
        })
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(true);
        setWhatsappSent(!!data.notifications?.whatsappSent);
        setTimeout(() => { onSuccess(); onClose(); resetForm(); }, 2000);
      } else setError(data.error || 'Error creando turno');
    } catch { setError('Error de conexión'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setFormData({
      professionalId: preselectedProfessional || '', locationId: '', serviceId: '', serviceIds: [],
      appointmentDate: preselectedDate || getTodayStr(), appointmentTime: '',
      clientName: '', clientPhone: '', clientEmail: '', notes: ''
    });
    setSuccess(false); setWhatsappSent(false); setError('');
    setLocations([]); setSchedule([]); setBlockedDates([]); setPaymentConfig(null);
    setBookingExtraFields(null); setPatientExtraData({});
    setIsOverride(false); setBookedSlots({});
    setComprobanteFile(null);
    setAllPatients([]); setPatientSuggestions([]); setShowPatientSuggestions(false);
  };

  const getDisplayDate = () => {
    if (!formData.appointmentDate) return '';
    try {
      const p = formData.appointmentDate.split('-');
      return new Date(parseInt(p[0]), parseInt(p[1]) - 1, parseInt(p[2]), 12).toLocaleDateString('es-AR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return formData.appointmentDate; }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-modal-in">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-white" />
            <h2 className="text-lg font-bold text-white">Nuevo Turno</h2>
          </div>
          <button onClick={() => { onClose(); resetForm(); }} className="p-1.5 hover:bg-white/20 rounded-full transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{isOverride ? '¡Sobreturno Creado!' : '¡Turno Creado!'}</h3>
            <p className="text-gray-600 mb-3">{isOverride ? 'El sobreturno se agendó correctamente' : 'El turno se agendó correctamente'}</p>
            <div className="bg-gray-50 rounded-lg p-4 mb-3 text-left">
              <p className="text-sm text-gray-700"><span className="font-medium">📅 Fecha:</span> {getDisplayDate()}</p>
              <p className="text-sm text-gray-700 mt-1"><span className="font-medium">🕐 Hora:</span> {formData.appointmentTime} hs</p>
              <p className="text-sm text-gray-700 mt-1"><span className="font-medium">👤 Paciente:</span> {formData.clientName}</p>
              {(() => {
                const selectedServiceNames = services.filter(s => formData.serviceIds.includes(s.id)).map(s => s.name);
                if (selectedServiceNames.length > 0) {
                  return <p className="text-sm text-gray-700 mt-1"><span className="font-medium">🩺 Servicio{selectedServiceNames.length > 1 ? 's' : ''}:</span> {selectedServiceNames.join(' + ')}</p>;
                }
                const singleService = services.find(s => String(s.id) === String(formData.serviceId));
                if (singleService) {
                  return <p className="text-sm text-gray-700 mt-1"><span className="font-medium">🩺 Servicio:</span> {singleService.name}</p>;
                }
                return null;
              })()}
              {formData.locationId && locations.length > 0 && (
                <p className="text-sm text-gray-700 mt-1">
                  <span className="font-medium">📍 Sede:</span> {locations.find(l => l.id === formData.locationId)?.name || ''}
                </p>
              )}
            </div>
            <div className="flex items-center justify-center gap-2 text-sm">
              {whatsappSent ? (
                <span className="flex items-center gap-1 text-green-600"><MessageCircle className="w-4 h-4" /> WhatsApp enviado ✓</span>
              ) : formData.clientPhone ? (
                <span className="flex items-center gap-1 text-yellow-600"><MessageCircle className="w-4 h-4" /> Notificación en proceso...</span>
              ) : (
                <span className="flex items-center gap-1 text-gray-400"><MessageCircle className="w-4 h-4" /> Sin teléfono - no se envió WhatsApp</span>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" /><span className="text-sm">{error}</span>
              </div>
            )}

            {/* Profesional */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Profesional *</label>
              <select name="professionalId" value={formData.professionalId} onChange={handleProfessionalChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" required>
                <option value="">Seleccionar profesional</option>
                {professionals.map(prof => (
                  <option key={prof.professional_id} value={prof.professional_id}>{prof.nombre} {prof.apellido} - {prof.especialidad}</option>
                ))}
              </select>
            </div>

            {/* ⚠️ Aviso de seña si el profesional trabaja con seña */}
            {paymentConfig && (paymentConfig.tipo_cobro === 'solo_sena' || paymentConfig.tipo_cobro === 'consulta_completa') && (
              <div className={`p-3 rounded-lg flex items-start gap-2 ${
                paymentConfig.tipo_cobro === 'solo_sena'
                  ? 'bg-amber-50 border border-amber-200 text-amber-800'
                  : 'bg-blue-50 border border-blue-200 text-blue-800'
              }`}>
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  {paymentConfig.tipo_cobro === 'solo_sena' ? (
                    <>
                      <p className="font-semibold">💰 Este profesional cobra seña ({paymentConfig.porcentaje_sena}%)</p>
                      <p className="text-xs mt-1 opacity-80">
                        Los pacientes que reservan online deben pagar una seña del {paymentConfig.porcentaje_sena}% antes de confirmar el turno.
                        {paymentConfig.acepta_transferencia && ' Acepta transferencia.'}
                        {paymentConfig.acepta_mercadopago && ' Acepta Mercado Pago.'}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold">💳 Este profesional cobra consulta completa online</p>
                      <p className="text-xs mt-1 opacity-80">
                        Los pacientes que reservan online deben pagar el total de la consulta antes de confirmar.
                        {paymentConfig.acepta_transferencia && ' Acepta transferencia.'}
                        {paymentConfig.acepta_mercadopago && ' Acepta Mercado Pago.'}
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Sede (solo si hay más de una) */}
            {formData.professionalId && locations.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="w-3.5 h-3.5 inline mr-1" /> Sede *
                </label>
                {loadingLocations ? (
                  <div className="flex items-center gap-2 text-gray-500 py-2"><Loader className="w-4 h-4 animate-spin" /> Cargando sedes...</div>
                ) : (
                  <select value={formData.locationId} onChange={handleLocationChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    required>
                    <option value="">Seleccionar sede</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}{loc.city ? ` - ${loc.city}` : ''}{loc.address ? ` (${loc.address})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Servicio(s) - multi-selección */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Servicio(s) *</label>
              {loadingServices ? (
                <div className="flex items-center gap-2 text-gray-500 py-2"><Loader className="w-4 h-4 animate-spin" /> Cargando servicios...</div>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {services.map(s => {
                    const isChecked = formData.serviceIds.includes(s.id);
                    return (
                      <label key={s.id} className={`flex items-center gap-2 p-1.5 rounded-md cursor-pointer hover:bg-gray-50 text-sm ${isChecked ? 'bg-blue-50 border border-blue-200' : ''}`}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            const newIds = isChecked
                              ? formData.serviceIds.filter(id => id !== s.id)
                              : [...formData.serviceIds, s.id];
                            setFormData(prev => ({
                              ...prev,
                              serviceIds: newIds,
                              serviceId: newIds[0] || ''
                            }));
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="flex-1">{s.name}</span>
                        <span className="text-gray-500 text-xs">{s.duration}min</span>
                        <span className="text-gray-700 font-medium text-xs">${s.price}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {formData.serviceIds.length > 1 && (
                <div className="mt-1.5 p-2 bg-blue-50 rounded-lg text-xs text-blue-700">
                  {formData.serviceIds.length} servicios | Duración total: {services.filter(s => formData.serviceIds.includes(s.id)).reduce((sum, s) => sum + s.duration, 0)} min | Total: ${services.filter(s => formData.serviceIds.includes(s.id)).reduce((sum, s) => sum + (parseFloat(String(s.price)) || 0), 0)}
                </div>
              )}
            </div>

            {/* Calendario (requiere sede si hay múltiples) */}
            {formData.professionalId && (locations.length <= 1 || formData.locationId) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" /> Fecha *
                </label>
                {loadingSchedule ? (
                  <div className="flex items-center gap-2 text-gray-500 py-4 justify-center"><Loader className="w-4 h-4 animate-spin" /> Cargando disponibilidad...</div>
                ) : (
                  <CustomCalendar selectedDate={formData.appointmentDate} onDateSelect={handleDateSelect} professionalSchedule={schedule} blockedDates={blockedDates} specialDayDates={specialDayDates} onlySpecialDays={onlySpecialDays} serviceAvailableDates={serviceAvailableDates} motivos={motivosDias} maxDate={(() => { const d = new Date(); d.setDate(d.getDate() + maxAdvanceBooking); return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`; })()} />
                )}
                {formData.appointmentDate && <p className="text-xs text-blue-600 mt-1 font-medium">📅 {getDisplayDate()}</p>}
              </div>
            )}

            {/* Horarios */}
            {formData.appointmentDate && formData.professionalId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2"><Clock className="w-4 h-4 inline mr-1" /> Horario *</label>
                {loadingSlots ? (
                  <div className="flex items-center gap-2 text-gray-500 py-3 justify-center">
                    <Loader className="w-4 h-4 animate-spin" /> Consultando disponibilidad...
                  </div>
                ) : (availableTimeSlots.length > 0 || (maxOverridesPerSlot > 0 && Object.keys(bookedSlots).length > 0)) ? (
                  <>
                    <div className="grid grid-cols-4 gap-1.5 max-h-32 overflow-y-auto">
                      {/* Slots libres */}
                      {availableTimeSlots.map(slot => (
                        <button key={slot} type="button" onClick={() => { setFormData(prev => ({ ...prev, appointmentTime: slot })); setIsOverride(false); }}
                          className={`py-1.5 px-2 text-xs rounded-lg border transition-all font-medium ${
                            formData.appointmentTime === slot && !isOverride
                              ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                          }`}>{slot}</button>
                      ))}
                      {/* Slots ocupados con posibilidad de sobreturno */}
                      {maxOverridesPerSlot > 0 && Object.entries(bookedSlots)
                        .filter(([time, count]) => count <= maxOverridesPerSlot && !availableTimeSlots.includes(time))
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([time, count]) => (
                          <button key={`st-${time}`} type="button"
                            onClick={() => { setFormData(prev => ({ ...prev, appointmentTime: time })); setIsOverride(true); }}
                            className={`py-1.5 px-2 text-xs rounded-lg border-2 border-dashed transition-all font-medium ${
                              formData.appointmentTime === time && isOverride
                                ? 'bg-orange-500 text-white border-orange-600 shadow-md'
                                : 'bg-orange-50 text-orange-700 border-orange-300 hover:bg-orange-100'
                            }`}
                            title={`Sobreturno (${count}/${maxOverridesPerSlot + 1} turnos)`}
                          >
                            {time} <span className="text-[10px]">ST</span>
                          </button>
                        ))
                      }
                    </div>
                    {/* Banner sobreturno seleccionado */}
                    {isOverride && formData.appointmentTime && (
                      <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                        <p className="text-xs text-orange-700">
                          <strong>Sobreturno:</strong> Ya hay {bookedSlots[formData.appointmentTime] || 1} turno(s) a las {formData.appointmentTime}. Se creará un turno adicional.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-3 text-sm text-amber-600 bg-amber-50 rounded-lg">
                    ⚠️ No hay horarios disponibles para esta fecha
                  </div>
                )}
              </div>
            )}

            {/* Datos del Paciente */}
            <div className="pt-3 border-t border-gray-200">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-1"><User className="w-4 h-4" /> Datos del Paciente</h3>
              <div className="space-y-3">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo *</label>
                  {/* name no-estándar + autoComplete off para que Chrome NO muestre su
                      propio historial encima de nuestro autocompletado de pacientes */}
                  <input type="text" name="patient-name-search" value={formData.clientName}
                    onChange={(e) => setFormData(prev => ({ ...prev, clientName: e.target.value }))}
                    placeholder="Juan Pérez"
                    autoComplete="off" autoCorrect="off" spellCheck={false}
                    onFocus={() => { if (patientSuggestions.length > 0) setShowPatientSuggestions(true); }}
                    onBlur={() => setTimeout(() => setShowPatientSuggestions(false), 150)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" required />
                  {showPatientSuggestions && patientSuggestions.length > 0 && (
                    <div className="absolute z-30 left-0 right-0 mt-2 bg-white border border-gray-100 rounded-xl shadow-xl ring-1 ring-black/5 overflow-hidden">
                      <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 bg-gray-50/80 border-b border-gray-100 flex items-center gap-1.5">
                        <User className="w-3 h-3" /> Pacientes anteriores
                      </div>
                      <div className="max-h-60 overflow-y-auto py-1">
                        {patientSuggestions.map((p, i) => {
                          const initials = (p.clientName || '?').trim().split(/\s+/).slice(0, 2).map((w: string) => w[0]).join('').toUpperCase();
                          return (
                            <button
                              key={i}
                              type="button"
                              onMouseDown={(e) => { e.preventDefault(); selectPatient(p); }}
                              className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-blue-50 transition-colors group"
                            >
                              <span className="w-8 h-8 flex-shrink-0 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 text-white text-xs font-bold flex items-center justify-center shadow-sm">
                                {initials || '?'}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block font-medium text-gray-800 text-sm truncate group-hover:text-blue-700">{p.clientName}</span>
                                <span className="block text-xs text-gray-500 truncate flex items-center gap-1">
                                  {p.clientPhone ? (<><Phone className="w-3 h-3 inline" /> {p.clientPhone}</>) : <span className="italic text-gray-400">sin teléfono</span>}
                                  {p.clientEmail && (<><span className="text-gray-300">•</span> <Mail className="w-3 h-3 inline" /> {p.clientEmail}</>)}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1"><Phone className="w-3.5 h-3.5 inline mr-1" /> Teléfono</label>
                    <input type="tel" name="clientPhone" value={formData.clientPhone} onChange={handleChange} placeholder="3794123456"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                    <p className="text-xs text-gray-400 mt-0.5">Para enviar WhatsApp</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1"><Mail className="w-3.5 h-3.5 inline mr-1" /> Email <span className="text-gray-400 text-xs">(opcional)</span></label>
                    <input type="email" name="clientEmail" value={formData.clientEmail} onChange={handleChange} placeholder="Si no tiene, dejar vacío"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1"><FileText className="w-3.5 h-3.5 inline mr-1" /> Notas</label>
                  <textarea name="notes" value={formData.notes} onChange={handleChange} placeholder="Observaciones adicionales..." rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm" />
                </div>

                {/* 🆕 Comprobante de pago (opcional). Si se sube, el turno queda con el pago verificado. */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FileText className="w-3.5 h-3.5 inline mr-1" /> Comprobante de pago <span className="text-gray-400 text-xs">(opcional)</span>
                  </label>
                  {comprobanteFile ? (
                    <div className="flex items-center justify-between gap-2 px-3 py-2 border border-green-200 bg-green-50 rounded-lg text-sm">
                      <span className="truncate text-green-700 flex items-center gap-1">
                        <Check className="w-4 h-4 flex-shrink-0" /> {comprobanteFile.name}
                      </span>
                      <button type="button" onClick={() => setComprobanteFile(null)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => setComprobanteFile(e.target.files?.[0] || null)}
                      className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">Si lo cargás, el turno queda con el pago verificado.</p>
                </div>
              </div>

              {/* Campos dinámicos extra del profesional */}
              {bookingExtraFields && bookingExtraFields.fields.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Datos adicionales del paciente</h4>
                  <div className="space-y-3">
                    {bookingExtraFields.fields.map((field: any) => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label} {field.required && <span className="text-red-500">*</span>}
                        </label>
                        {field.type === 'textarea' ? (
                          <textarea
                            value={patientExtraData[field.key] || ''}
                            onChange={(e) => setPatientExtraData(prev => ({ ...prev, [field.key]: e.target.value }))}
                            rows={2}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                            placeholder={field.label}
                          />
                        ) : field.type === 'select' && field.options ? (
                          <select
                            value={patientExtraData[field.key] || ''}
                            onChange={(e) => setPatientExtraData(prev => ({ ...prev, [field.key]: e.target.value }))}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          >
                            <option value="">Seleccionar...</option>
                            {field.options.map((opt: string) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={field.type === 'date' ? 'date' : 'text'}
                            value={patientExtraData[field.key] || ''}
                            onChange={(e) => setPatientExtraData(prev => ({ ...prev, [field.key]: e.target.value }))}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder={field.label}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Botones */}
            <div className="flex gap-3 pt-3">
              <button type="button" onClick={() => { onClose(); resetForm(); }}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium">Cancelar</button>
              <button type="submit" disabled={loading || !formData.professionalId || (formData.serviceIds.length === 0 && !formData.serviceId) || !formData.clientName || !formData.appointmentTime}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium">
                {loading ? (<><Loader className="w-4 h-4 animate-spin" /> {uploadingComprobante ? 'Subiendo comprobante...' : 'Creando...'}</>) : (<><Check className="w-4 h-4" /> Crear Turno</>)}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default NewAppointmentModal;